import { Component, EventEmitter, OnDestroy, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { forkJoin } from 'rxjs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { UserService } from 'app/core/services/user.service';
import { AppConstants } from 'app/app.constants';
import { TimeoutEnum } from 'app/core/enum/timeout.enum';
import { ActiveView } from '../../login-page.component';

const TOAST_POSITION = 'toast-top-full-width';

function passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
  const password = control.get('password');
  const confirmPassword = control.get('confirmPassword');
  if (
    password &&
    confirmPassword &&
    password.value &&
    confirmPassword.value &&
    password.value !== confirmPassword.value
  ) {
    return { passwordMismatch: true };
  }
  return null;
}

@Component({
  standalone: true,
  selector: 'app-register-form',
  templateUrl: './register-form.component.html',
  styleUrls: ['./register-form.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    TranslateModule,
  ],
})
export class RegisterFormComponent implements OnDestroy {
  @Output() changeView = new EventEmitter<ActiveView>();

  private redirectTimer?: ReturnType<typeof setTimeout>;
  private fb = inject(FormBuilder);
  private userService = inject(UserService);
  private translate = inject(TranslateService);
  private toastr = inject(ToastrService);

  isPasswordVisible = false;
  isConfirmPasswordVisible = false;
  isLoading = false;

  form = this.fb.group(
    {
      name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(150)]],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(16)]],
      confirmPassword: ['', [Validators.required]],
      termsAccepted: [false, [Validators.requiredTrue]],
    },
    { validators: passwordMatchValidator },
  );

  togglePassword(): void {
    this.isPasswordVisible = !this.isPasswordVisible;
  }

  ngOnDestroy(): void {
    clearTimeout(this.redirectTimer);
  }

  toggleConfirmPassword(): void {
    this.isConfirmPasswordVisible = !this.isConfirmPasswordVisible;
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    const name = this.form.value.name!;
    const email = this.form.value.email!;
    const password = this.form.value.password!;
    const confirmPassword = this.form.value.confirmPassword!;

    const lang =
      localStorage.getItem(AppConstants.KEYS_LOCAL_STORAGE.ISO_LANG) ||
      AppConstants.LANGUAGES.PT_BR;

    const user = {
      name,
      id: email,
      email,
      password,
      passwordConfirmation: confirmPassword,
      lang,
    };

    this.userService.saveUsuarioFreeTier(user).subscribe({
      next: () => {
        this.form.reset();
        this.isLoading = false;
        forkJoin({
          success: this.translate.get('register.title-mail-post-registration'),
          warning: this.translate.get('register.title-unactivated-account-warning'),
        }).subscribe(({ success, warning }) => {
          this.toastr.success(success, '', { positionClass: TOAST_POSITION });
          this.toastr.warning(warning, '', { positionClass: TOAST_POSITION });
        });
        this.redirectTimer = setTimeout(() => this.changeView.emit('login'), TimeoutEnum.MediumLong);
      },
      error: (errorMsg) => {
        this.isLoading = false;
        if (errorMsg?.error?.name === 'Conflict') {
          this.translate
            .get('global.alert.msg-account-error')
            .subscribe((res: string) => {
              this.toastr.error(res, '', {
                positionClass: TOAST_POSITION,
              });
            });
        } else {
          this.translate
            .get('global.validate.registration-failed')
            .subscribe((res: string) => {
              this.toastr.error(res, '', {
                positionClass: TOAST_POSITION,
              });
            });
        }
      },
    });
  }
}
